import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { EmptyState } from "@/components/common/EmptyState";
import { toast } from "sonner";
import { LifeBuoy, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

const NONE = "__none__";

export default function KeyContacts() {
  const [orgs, setOrgs] = useState([]);
  const [org, setOrg] = useState("");
  const [topics, setTopics] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    api.get("/organizations").then((r) => {
      const list = r.data.organizations || [];
      setOrgs(list);
      const d88 = list.find((o) => o.name?.includes("District 88")) || list[0];
      if (d88) setOrg(d88.id);
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const load = useCallback(async () => {
    if (!org) return;
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get("/key-contacts/topics", { params: { organization_id: org, include_inactive: true } }),
        api.get("/key-contacts/staff", { params: { organization_id: org } }),
      ]);
      setTopics(t.data.items || []);
      setStaff(s.data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [org]);
  useEffect(() => { load(); }, [load]);

  const setDefault = async (t, val) => {
    try {
      await api.patch(`/key-contacts/topics/${t.id}`, { default_person_id: val === NONE ? null : val });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleActive = async (t) => {
    try { await api.patch(`/key-contacts/topics/${t.id}`, { active: !t.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (t) => {
    if (!window.confirm(`Delete topic "${t.name}"?`)) return;
    try { await api.delete(`/key-contacts/topics/${t.id}`); toast.success("Topic deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const add = async () => {
    if (!newName.trim()) return;
    try { await api.post("/key-contacts/topics", { organization_id: org, name: newName.trim() }); setNewName(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const move = async (idx, dir) => {
    const movable = topics.filter((t) => t.role !== "district_manager");
    const j = idx + dir;
    if (j < 0 || j >= movable.length) return;
    const arr = [...movable];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setTopics([...arr, ...topics.filter((t) => t.role === "district_manager")]);
    try { await api.post("/key-contacts/topics/reorder", { organization_id: org, ordered_ids: arr.map((t) => t.id) }); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const movable = topics.filter((t) => t.role !== "district_manager");
  const dm = topics.filter((t) => t.role === "district_manager");

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-[#0D9488]" /> District Key Contacts
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Configure "Who should I contact about this?" defaults per District.</p>
        </div>
        <Select value={org} onValueChange={setOrg}>
          <SelectTrigger className="w-64" data-testid="kc-district-select"><SelectValue placeholder="Select District" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72">{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? <p className="text-sm text-slate-400 mt-8">Loading…</p> : topics.length === 0 ? (
        <div className="mt-10"><EmptyState icon={LifeBuoy} title="No topics" description="Add a Key Contact topic to get started." /></div>
      ) : (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="kc-topics-list">
          {[...movable, ...dm].map((t, i) => {
            const idx = movable.findIndex((m) => m.id === t.id);
            const isDM = t.role === "district_manager";
            return (
              <div key={t.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`kc-topic-${t.id}`}>
                <div className="flex flex-col gap-0.5">
                  <button disabled={isDM || idx <= 0} onClick={() => move(idx, -1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`kc-up-${t.id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button disabled={isDM || idx >= movable.length - 1} onClick={() => move(idx, 1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`kc-down-${t.id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm font-medium text-slate-800">{t.name}</p>
                  {t.role && <p className="text-xs text-[#0D9488] font-medium capitalize">{t.role.replace("_", " ")}{isDM ? " · always last" : ""}</p>}
                </div>
                <Select value={t.default_person_id || NONE} onValueChange={(v) => setDefault(t, v)}>
                  <SelectTrigger className="w-56" data-testid={`kc-default-${t.id}`}><SelectValue placeholder="Not Assigned" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value={NONE}>Not Assigned</SelectItem>
                    {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Switch checked={t.active !== false} onCheckedChange={() => toggleActive(t)} data-testid={`kc-active-${t.id}`} />
                  <span className="text-xs text-slate-500">Active</span>
                </div>
                {!isDM && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(t)} data-testid={`kc-del-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            );
          })}
        </div>
      )}

      {org && (
        <div className="mt-4 flex gap-2 max-w-md">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New topic name…" data-testid="kc-new-name" />
          <Button onClick={add} data-testid="kc-add-topic" className="bg-[#006090] hover:bg-[#004E75]"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
      )}
    </div>
  );
}
